import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { canOpenNavigationPath, getNavigationLabel, getNavigationLink, hasManagementAccess, hasWorkerAccess, isRestaurantOperatingContext } from '../constants/management';
import { normalizeRoleNames } from '../utils/roleNames';
import { OPERATING_GROUP_STORAGE_KEY } from '../api/api';
import { useOperatingContext } from '../utils/operatingContextLifecycle';
import SelfAttendance from '../components/SelfAttendance';
import './WorkspacePage.css';

type WorkspaceAction = {
  label: string;
  description: string;
  path: string;
};

type WorkspaceGroup = {
  title: string;
  description: string;
  actions: WorkspaceAction[];
};

const action = (path: string, description: string): WorkspaceAction => ({
  path, description, label: getNavigationLabel(path),
});

export const WORKSPACE_GROUPS: WorkspaceGroup[] = [
  {
    title: 'Події та клієнти',
    description: 'Проведіть подію від першого контакту до фінального результату.',
    actions: [
      action('/management/crm-dashboard', 'Прострочені контакти, задачі та найближчі події.'),
      action('/management/event-orders', 'Меню, готовність, персонал і фінансові дані події.'),
      action('/management/customers', 'Картки клієнтів, етапи продажу та історія взаємодій.'),
    ],
  },
  {
    title: 'Кухня та виробництво',
    description: 'Плануйте підготовку, рецептури та виконання кухонних задач.',
    actions: [
      action('/management/kitchen-execution', 'Спільний бланк по операціях, команда замовлення та контроль готовності.'),
      action('/management/semi-finished', 'Залишки, виробничі партії та потреби подій.'),
      action('/management/semi-finished-production', 'Калькулятор партій, доступні залишки та завдання відповідальним.'),
      action('/management/preparation-worksheets', 'Планові операції для підготовки до подій.'),
      action('/management/dishes', 'Склад, порції, собівартість і алергени.'),
    ],
  },
  {
    title: 'Склад та закупівлі',
    description: 'Контролюйте доступність продуктів і рух запасів.',
    actions: [
      action('/management/purchase-planning', 'MRP-рекомендації для підтверджених подій.'),
      action('/management/order-supply', 'Дефіцит по подіях і зведений список продуктів для закупівлі.'),
      action('/management/inventory-balance', 'Поточні баланси та ризики дефіциту.'),
      action('/management/inventory-receipts', 'Приймання, партії та коригування запасів.'),
    ],
  },
  {
    title: 'Фінанси та контроль',
    description: 'Перевіряйте маржу, платежі, закупівлі та контрольні журнали.',
    actions: [
      action('/analytics/event-finance', 'План-факт виручки, витрат і прибутку.'),
      action('/analytics/procurement-finance', 'Постачальники, платежі та банківські перевірки.'),
      action('/admin/audit-logs', 'Хто і коли змінював критичні дані.'),
    ],
  },
];

export const getVisibleWorkspaceGroups = (
  permissions: string[],
  selectedGroupId: string | null = null,
  restaurantAuditReadsEnabled = false,
): WorkspaceGroup[] =>
  WORKSPACE_GROUPS
    .map(group => ({
      ...group,
      actions: group.actions.filter(action =>
        canOpenNavigationPath(action.path, permissions, selectedGroupId, restaurantAuditReadsEnabled) &&
        (!isRestaurantOperatingContext(selectedGroupId) ||
          getNavigationLink(action.path)?.contextSupport !== 'legacy')),
    }))
    .filter(group => group.actions.length > 0);

const WorkspaceContent: React.FC<{ storageKey: string }> = ({ storageKey }) => {
  const { roles, permissions, username } = useAppSelector(state => state.auth);
  const [selectedGroupId] = useState(() => localStorage.getItem(OPERATING_GROUP_STORAGE_KEY));
  const { restaurantAuditReadsEnabled } = useOperatingContext();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [storageError, setStorageError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const normalizedRoles = normalizeRoleNames(roles);
  const management = hasManagementAccess(normalizedRoles, permissions);
  const worker = hasWorkerAccess(normalizedRoles, permissions);
  const visibleGroups = useMemo(() => getVisibleWorkspaceGroups(
    permissions, selectedGroupId, restaurantAuditReadsEnabled),
  [permissions, selectedGroupId, restaurantAuditReadsEnabled]);
  const actions = visibleGroups.flatMap(group => group.actions);
  const pinnedActions = actions.filter(action => favorites.includes(action.path));

  useEffect(() => {
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(stored) || !stored.every(path => typeof path === 'string'))
        throw new Error('Invalid workspace preferences');
      setFavorites(stored);
    } catch (error) {
      console.error('Не вдалося прочитати налаштування робочого простору.', error);
      setStorageError('Не вдалося прочитати налаштування. Повторно оберіть швидкі переходи.');
    }
  }, [storageKey]);

  const toggleFavorite = (path: string) => {
    const next = favorites.includes(path) ? favorites.filter(item => item !== path) : [...favorites, path];
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      setFavorites(next);
      setStorageError('');
    } catch (error) {
      console.error('Не вдалося зберегти налаштування робочого простору.', error);
      setStorageError('Браузер не дозволив зберегти налаштування. Зміни не збережено.');
    }
  };

  return (
    <main className="workspace-page">
      <div className="workspace-page__header">
        <h1>Мій робочий простір</h1>
        <button type="button" className="workspace-page__settings-toggle" aria-expanded={settingsOpen}
          aria-controls="workspace-settings" onClick={() => setSettingsOpen(open => !open)}>
          {settingsOpen ? 'Закрити налаштування' : 'Налаштувати швидкі переходи'}
        </button>
      </div>
      <p className="workspace-page__description">
        {username ? `Вітаємо, ${username}. ` : ''}
        Показано лише ті робочі процеси, до яких у вас є доступ.
      </p>
      <div className="workspace-page__badges">
        <span>
          Ролі: {normalizedRoles.length > 0 ? normalizedRoles.join(', ') : 'не визначено'}
        </span>
        <span className={`workspace-page__access workspace-page__access--${management ? 'management' : worker ? 'worker' : 'basic'}`}>
          {management ? 'Доступ керівництва' : worker ? 'Операційний доступ' : 'Базовий доступ'}
        </span>
      </div>

      <SelfAttendance />
      {storageError && <p role="alert">{storageError}</p>}
      {settingsOpen && (
        <section id="workspace-settings" className="workspace-page__settings" aria-labelledby="workspace-settings-title">
          <h2 id="workspace-settings-title">Швидкі переходи</h2>
          <p>Оберіть потрібні процеси. Налаштування зберігаються лише для вашого акаунта в цьому браузері та не змінюють права доступу чи контекст роботи.</p>
          <div className="workspace-page__settings-grid">
            {actions.map(action => (
              <label key={action.path}>
                <input type="checkbox" checked={favorites.includes(action.path)} onChange={() => toggleFavorite(action.path)} />
                <span>{action.label}</span>
              </label>
            ))}
          </div>
        </section>
      )}
      <section className="workspace-page__section" aria-labelledby="workspace-pinned-title">
        <h2 id="workspace-pinned-title">Мої швидкі переходи</h2>
        {pinnedActions.length > 0 ? (
          <div className="workspace-page__grid">
            {pinnedActions.map(action => <Link className="workspace-page__card" key={action.path} to={action.path}>{action.label} →</Link>)}
          </div>
        ) : <p className="workspace-page__description">Закріпіть найчастіші процеси через «Налаштувати швидкі переходи».</p>}
      </section>
      {visibleGroups.map(group => (
        <section key={group.title} className="workspace-page__section">
          <h2>{group.title}</h2>
          <p className="workspace-page__description">{group.description}</p>
          <div className="workspace-page__grid">
            {group.actions.map(action => (
              <Link key={action.path} to={action.path} className="workspace-page__card">
                <strong>{action.label} →</strong>
                <div className="workspace-page__card-description">{action.description}</div>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {visibleGroups.length === 0 && (
        <div role="status" className="workspace-page__empty">
          Для вашої ролі ще не налаштовано робочих процесів. Зверніться до адміністратора.
        </div>
      )}
    </main>
  );
};

const WorkspacePage: React.FC = () => {
  const username = useAppSelector(state => state.auth.username);
  const storageKey = `crm4chef:workspace:v1:${encodeURIComponent(username || '')}`;
  return <WorkspaceContent key={storageKey} storageKey={storageKey} />;
};

export default WorkspacePage;
